import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isPlatformAdmin } from '../utils/adminAccess';
import { PageHeader } from '../components/PageHeader';

export const AdminPortalPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = isPlatformAdmin(user?.email || '');

  if (!isAdmin) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-star-line"
        title="Admin Portal"
        subtitle="Framework and control library management"
      />

      <div className="np-grid np-grid-3">
        <div className="np-card h-100 d-flex flex-column">
          <div className="np-card-body text-center d-flex flex-column flex-grow-1">
            <span className="np-icon-badge-lg np-tone-primary mx-auto mb-3">
              <i className="ri-book-2-line"></i>
            </span>
            <h5 className="mb-2">Framework Management</h5>
            <p className="np-sub mb-3 flex-grow-1">Upload and manage control frameworks (ISO 27001, NIST, SOC 2, etc.)</p>
            <Link to="/admin/frameworks" className="np-btn np-btn-primary w-100">
              <i className="ri-settings-3-line"></i>Manage Frameworks
            </Link>
          </div>
        </div>

        <div className="np-card h-100 d-flex flex-column">
          <div className="np-card-body text-center d-flex flex-column flex-grow-1">
            <span className="np-icon-badge-lg np-tone-success mx-auto mb-3">
              <i className="ri-links-line"></i>
            </span>
            <h5 className="mb-2">Framework Mappings</h5>
            <p className="np-sub mb-3 flex-grow-1">Create and manage crosswalk mappings between frameworks</p>
            <Link to="/admin/mappings" className="np-btn np-btn-success w-100">
              <i className="ri-links-line"></i>Map Controls
            </Link>
          </div>
        </div>

        <div className="np-card h-100 d-flex flex-column opacity-75">
          <div className="np-card-body text-center d-flex flex-column flex-grow-1">
            <span className="np-icon-badge-lg np-tone-info mx-auto mb-3">
              <i className="ri-layout-grid-line"></i>
            </span>
            <h5 className="mb-2">Template Management</h5>
            <p className="np-sub mb-3 flex-grow-1">Create control matrix templates from frameworks</p>
            <button className="np-btn np-btn-ghost w-100" disabled>
              <span className="np-chip np-tone-warning">Coming Soon</span>
              Manage Templates
            </button>
          </div>
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-head">
          <h2><i className="ri-information-line"></i> About Admin Portal</h2>
        </div>
        <div className="np-card-body">
          <p className="mb-3">The Admin Portal allows Mercell employees to manage the universal control library and framework mappings. Changes made here are reflected across all tenant accounts.</p>
          <div className="np-grid np-grid-3">
            <div className="d-flex align-items-start">
              <i className="ri-book-2-line text-primary me-2 mt-1"></i>
              <div>
                <strong>Framework Management</strong>
                <p className="np-hint mb-0">Upload control frameworks from CSV files, view and edit framework details</p>
              </div>
            </div>
            <div className="d-flex align-items-start">
              <i className="ri-links-line text-success me-2 mt-1"></i>
              <div>
                <strong>Framework Mappings</strong>
                <p className="np-hint mb-0">Create equivalency mappings between controls across different frameworks</p>
              </div>
            </div>
            <div className="d-flex align-items-start">
              <i className="ri-layout-grid-line text-info me-2 mt-1"></i>
              <div>
                <strong>Template Management</strong>
                <p className="np-hint mb-0">Build control matrix templates that tenants can use to quickly set up their compliance programs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
